import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  expensesUrl, expenseByIdUrl, expenseApproveUrl, expenseCategoriesUrl, branchesUrl,
} from '../../BaseURL';
import Modal from '../../components/Modal';
import { fmtMoney, fmtDate, titleCase } from '../../utils/format';
import { toastSuccess, swalDangerConfirm, showAxiosError } from '../../utils/swal';

const STATUS_BADGE = { recorded: 'badge-gold', approved: 'badge-green', rejected: 'badge-red' };
const PAYMENT_METHODS = ['cash', 'momo', 'card', 'bank', 'cheque'];

const today = () => new Date().toISOString().slice(0, 10);

const Expenses = () => {
  const [me] = useState(() => { try { return JSON.parse(localStorage.getItem('user') || 'null'); } catch (_) { return null; } });
  const isManager = me?.role === 'super_admin' || me?.role === 'branch_manager';
  const isSuper = me?.role === 'super_admin';
  const [rows, setRows] = useState([]);
  const [cats, setCats] = useState([]);
  const [branches, setBranches] = useState([]);
  const [filter, setFilter] = useState('all'); // all | recorded | approved
  const [search, setSearch] = useState('');

  const [open, setOpen] = useState(false);
  const empty = { branch_id: me?.branch_id || '', category_id: '', amount: '', description: '', payment_method: 'cash', occurred_on: today() };
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    const calls = [axios.get(expensesUrl), axios.get(expenseCategoriesUrl)];
    if (isSuper) calls.push(axios.get(branchesUrl));
    const [e, c, b] = await Promise.all(calls);
    setRows(e.data?.results || []);
    setCats(c.data?.results || []);
    if (b) setBranches(b.data?.results || []);
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    let list = rows;
    if (filter !== 'all') list = list.filter((r) => r.status === filter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((r) => [r.description, r.category_name, r.branch_name].some((v) => String(v || '').toLowerCase().includes(q)));
    }
    return list;
  }, [rows, filter, search]);

  const totals = useMemo(() => ({
    recorded: rows.filter((r) => r.status === 'recorded').reduce((s, r) => s + Number(r.amount), 0),
    approved: rows.filter((r) => r.status === 'approved').reduce((s, r) => s + Number(r.amount), 0),
    all: rows.reduce((s, r) => s + Number(r.amount), 0),
  }), [rows]);

  const submit = async () => {
    setError(null); setSaving(true);
    try {
      await axios.post(expensesUrl, { ...form, amount: Number(form.amount) });
      setOpen(false); setForm(empty); await load();
      toastSuccess('Expense recorded');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed');
    } finally { setSaving(false); }
  };

  const approve = async (e) => {
    try {
      await axios.patch(expenseApproveUrl(e.id));
      toastSuccess('Expense approved');
      load();
    } catch (err) { showAxiosError(err); }
  };

  const remove = async (e) => {
    const ok = await swalDangerConfirm('Delete this expense?', 'This cannot be undone.', { confirmButtonText: 'Yes, delete' });
    if (!ok) return;
    try {
      await axios.delete(expenseByIdUrl(e.id));
      toastSuccess('Expense deleted');
      load();
    } catch (err) { showAxiosError(err); }
  };

  return (
    <>
      <div className="kpi-row">
        <div className="stat"><div className="ic">💳</div><div className="lbl">Total expenses</div><div className="val">{fmtMoney(totals.all)}</div><div className="hint">in current view</div></div>
        <div className="stat"><div className="ic">⏳</div><div className="lbl">Awaiting approval</div><div className="val" style={{ color: 'var(--warn)' }}>{fmtMoney(totals.recorded)}</div><div className="hint">recorded, pending</div></div>
        <div className="stat"><div className="ic">✅</div><div className="lbl">Approved</div><div className="val">{fmtMoney(totals.approved)}</div><div className="hint">approved spend</div></div>
        <div className="stat"><div className="ic">📁</div><div className="lbl">Categories</div><div className="val">{cats.length}</div><div className="hint">expense buckets</div></div>
      </div>

      <div className="panel">
        <div className="panel-head">
          <h2>Expenses</h2>
          <div className="panel-toolbar">
            <div style={{ display: 'flex', gap: 4, background: 'var(--bg-tint)', padding: 4, borderRadius: 8 }}>
              {[['all','All'],['recorded','Pending'],['approved','Approved']].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className="btn-ghost" style={{ border: 'none', background: filter === k ? '#fff' : 'transparent' }}>{l}</button>
              ))}
            </div>
            <input className="f-input" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 240 }} />
            <button className="btn-primary" onClick={() => { setForm(empty); setOpen(true); setError(null); }}>+ Record expense</button>
          </div>
        </div>

        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Date</th>{isSuper && <th>Branch</th>}<th>Category</th><th>Description</th>
                <th>Method</th><th className="right">Amount</th><th>Status</th><th></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id}>
                  <td>{e.occurred_on}</td>
                  {isSuper && <td>{e.branch_name}</td>}
                  <td>{e.category_name || '—'}</td>
                  <td><strong>{e.description}</strong><div style={{ fontSize: 12, color: 'var(--mute)' }}>by {e.recorded_by_name} · {fmtDate(e.created_at)}</div></td>
                  <td>{e.payment_method ? titleCase(e.payment_method) : '—'}</td>
                  <td className="right num"><strong>{fmtMoney(e.amount)}</strong></td>
                  <td><span className={`badge ${STATUS_BADGE[e.status] || 'badge-gray'}`}>{titleCase(e.status)}</span></td>
                  <td className="right" style={{ whiteSpace: 'nowrap' }}>
                    {e.status === 'recorded' && isManager && <button className="btn-link" onClick={() => approve(e)}>Approve</button>}
                    <button className="btn-link" style={{ color: 'var(--danger)' }} onClick={() => remove(e)}>Delete</button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={isSuper ? 8 : 7} style={{ color: 'var(--mute)', textAlign: 'center', padding: 24 }}>No expenses match this view.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <Modal
        title="Record expense" open={open} onClose={() => setOpen(false)}
        footer={
          <>
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn-primary" onClick={submit} disabled={saving || !form.amount || !form.description}>{saving ? 'Saving…' : 'Record expense'}</button>
          </>
        }
      >
        {error && <div className="notice">{error}</div>}
        <div className="f-grid f-grid-2">
          {isSuper && (
            <div className="f-row">
              <label className="f-label">Branch *</label>
              <select className="f-select" value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })}>
                <option value="">— Choose —</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          )}
          <div className="f-row">
            <label className="f-label">Date *</label>
            <input className="f-input" type="date" value={form.occurred_on} onChange={(e) => setForm({ ...form, occurred_on: e.target.value })} />
          </div>
          <div className="f-row">
            <label className="f-label">Category</label>
            <select className="f-select" value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}>
              <option value="">— None —</option>
              {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="f-row">
            <label className="f-label">Payment method</label>
            <select className="f-select" value={form.payment_method} onChange={(e) => setForm({ ...form, payment_method: e.target.value })}>
              {PAYMENT_METHODS.map((m) => <option key={m} value={m}>{titleCase(m)}</option>)}
            </select>
          </div>
          <div className="f-row">
            <label className="f-label">Amount (GHS) *</label>
            <input className="f-input" type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
          </div>
          <div className="f-row" style={{ gridColumn: '1 / -1' }}>
            <label className="f-label">Description *</label>
            <input className="f-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What was this expense for?" />
          </div>
        </div>
      </Modal>
    </>
  );
};

export default Expenses;
